import { Intent } from "@blueprintjs/core";
import classNames from "classnames";
import { FormMultiSelect, FormSelect, FormTextArea, FormTextInput } from "components/form-fields";
import { GridLayout } from "components/grid";
import { ShowHide } from "components/layout";
import { NotificationInline } from "components/notifications";
import { useValidation } from "hooks/useValidation";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { AssetSelection } from "components/Asset/AssetSelection";

export function TemplateDetails(
    {
        isReadOnly = false,
        isNew = true,
        showAssetTypeExclusionWarning = false,
        showAssetSubTypeExclusionWarning = false,
        assetOptions,
        loading,
        locationBusinessAreas,
        onChange,
        onValidationChange,
        saving,
        template,
        templateOptions
    }
) {
    const [isValid, errors, validate] = useValidation();
    const [subCategories, setSubCategories] = useState([]);
    const [category, setCategory] = useState("");

    useEffect(() => {
        const rules = [
            { fieldName: "name", required: true, maxLength: 255 },
            { fieldName: "introductionMessage", required: false, maxLength: 1000 }
        ]

        if (isNew) {
            rules.push({ fieldName: "category", required: true });
            rules.push({ fieldName: "subCategory", required: true });
        }

        validate(rules, template);
        const newCategory = calculateCategoryFromSubCategory(template.subCategory);
        if (newCategory && !category) {
            setCategory(newCategory);
        }
    }, [template, templateOptions]);

    useEffect(() => onValidationChange(isValid), [isValid]);

    useEffect(() => {
        if (category) {
            setSubCategories(templateOptions.templateCategories.find(c => c.key === category).subCategories)
        } else {
            setSubCategories([]);
        }
    }, [category, templateOptions])

    const setName = (name) => {
        const clone = {
            ...template,
            name: name
        };

        onChange(clone);
    }

    const setSubCategory = (subCategory) => {
        const clone = {
            ...template,
            subCategory: subCategory.id
        };

        onChange(clone);
    }

    const onCategoryChange = (newCategory) => {
        setCategory(newCategory);
        const clone = {
            ...template,
            category: newCategory
        };

        onChange(clone);
    }

    const onConditionChange = (newCondition) => {
        const clone = {
            ...template,
            conditions: newCondition
        };

        onChange(clone);
    }

    const calculateCategoryFromSubCategory = (subCategory) => {
        if (subCategory) {
            return templateOptions.templateCategories
                .find(c => c.subCategories.some(s => s.key === subCategory))
                ?.key;
        }

        return null;
    }

    const setIntroductionMessage = (message) => {
        const clone = {
            ...template,
            introductionMessage: message
        };

        onChange(clone);
    }

    const onConditionAdd = (item, name) => {
        const cloneConditions = {
            ...template?.conditions
        }

        const currentConditions = cloneConditions[name] ? cloneConditions[name] : []

        if (!currentConditions.some(c => c === item.id)) {
            cloneConditions[name] = [...currentConditions, item.id];
        }

        onConditionChange(cloneConditions);
    }

    const onConditionRemove = (item, name, allItems) => {
        const cloneConditions = {
            ...template?.conditions
        }

        const removedId = allItems.find(a => a.name === item).id;
        cloneConditions[name] = cloneConditions[name].filter(c => c !== removedId);

        onConditionChange(cloneConditions);
    }

    const mapSelectedValues = (allItems, selectedItems) => {
        if (selectedItems && allItems) {
            return selectedItems
                .map(i => allItems.find(a => a.id === i))
                .filter(i => i);
        }

        return [];
    }

    const renderLocationForm = () => {
        const mappedLocationBusinessAreas = locationBusinessAreas.map(x => ({
            id: x.businessArea,
            name: x.businessArea,
        }));

        return (
            <Fragment>
                <GridLayout numberOfColumns={isNew ? 3 : 1} gap={30}>
                    <div>
                        <FormMultiSelect
                            id="location-business-areas-select"
                            loading={loading}
                            disabled={isReadOnly || saving}
                            items={mappedLocationBusinessAreas}
                            onItemSelect={(item) => onConditionAdd(item, "locationBusinessAreas")}
                            onRemove={(item) => onConditionRemove(item, "locationBusinessAreas", mappedLocationBusinessAreas)}
                            placeholder="Please select"
                            headingText="Location Business Areas"
                            fill={isNew}
                            selectedValues={mapSelectedValues(mappedLocationBusinessAreas, template.conditions?.locationBusinessAreas)}>
                        </FormMultiSelect>
                    </div>
                </GridLayout>
            </Fragment>
        );
    };

    return (
        <div className={classNames({ "row": !isNew })}>
            <h2>Details</h2>
            <NotificationInline intent={Intent.WARNING} text={`An excluded asset type was previously selected as a condition and will be ignored when determining if a template is valid for an asset.${!isReadOnly ? ' Saving the template will remove the excluded type.' : ''}`} show={showAssetTypeExclusionWarning} />
            <NotificationInline intent={Intent.WARNING} text={`A no longer available asset subtype was previously selected as a condition and will be ignored when determining if a template is valid for an asset.${!isReadOnly ? ' Saving the template will remove the invalid subtype from the condition.' : ''}`} show={showAssetSubTypeExclusionWarning} />
            <FormTextInput
                id="template-name"
                headingText="Template name"
                value={template.name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter template name"
                fill={isNew}
                large
                disabled={isReadOnly || template.versionNumber > 1 || saving}
                loading={loading}
                dangerHelperText={errors["name"]}
            />
            <FormTextArea
                id="template-introduction-message"
                headingText="Introduction message"
                value={template.introductionMessage}
                onChange={(e) => setIntroductionMessage(e.target.value)}
                fill={isNew}
                disabled={isReadOnly || saving}
                loading={loading}
                dangerHelperText={errors["introductionMessage"]}
            />
            <ShowHide
                evaluator={isNew}
                show={(<Fragment>
                    <h2>Template Categories</h2>
                    <div className='spacer-bottom'>
                        <p>Once the template type and sub-type have been set they cannot be changed.</p>
                    </div>
                </Fragment>)}
            />
            <GridLayout numberOfColumns={isNew ? 3 : 1} gap={30}>
                <FormSelect
                    id="template-category"
                    headingText="Template category"
                    items={templateOptions.templateCategories.map(c => {
                        return {
                            id: c.key,
                            name: c.name
                        }
                    })}
                    selectedValue={category}
                    onItemSelect={(c) => onCategoryChange(c.id)}
                    placeholder="Please select"
                    dangerHelperText={errors["category"]}
                    fill={isNew}
                    disabled={!isNew || saving}
                    loading={loading}
                />
                <ShowHide
                    evaluator={!!category}
                    show={(<FormSelect
                        id="template-subcategory"
                        headingText="Template sub-category"
                        items={subCategories.map(c => {
                            return {
                                id: c.key,
                                name: c.name
                            }
                        })}
                        selectedValue={template.subCategory}
                        onItemSelect={setSubCategory}
                        placeholder="Please select"
                        dangerHelperText={errors["subCategory"]}
                        fill={isNew}
                        disabled={!isNew || saving}
                        loading={loading}
                    />)}
                />

            </GridLayout>
            <ShowHide
                evaluator={isNew && !!category}
                show={(<Fragment>
                    <h2>Template Category Settings</h2>
                    <div className='spacer-bottom'>
                        <p>The below settings are required to create the template but can be changed later through the template settings.</p>
                    </div>
                </Fragment>)}
            />
            <ShowHide
                evaluator={category === "Asset"}
                show={<AssetSelection
                    loading={loading}
                    saving={saving}
                    isReadOnly={isReadOnly}
                    onChange={onConditionChange}
                    conditions={template.conditions}
                    assetOptions={assetOptions}
                    isNew={isNew}
                />}
            />
            <ShowHide
                evaluator={category === "Location"}
                show={renderLocationForm()}
            />
            <ShowHide
                evaluator={!!template.relatedEventType}
                show={
                    <ShowHide
                        evaluator={!!template.relatedEventType}
                        show={
                            <NotificationInline
                                allowClose={false}
                                show
                                text={"This template is associated with an event type. " +
                                "Any changes made to this template will be reflected on associated events." }
                                intent="info"
                            />
                        }
                    />
                }
            />
        </div>
    );
}

TemplateDetails.propTypes = {
    template: PropTypes.object.isRequired,
    onChange: PropTypes.func.isRequired,
    loading: PropTypes.bool.isRequired,
    saving: PropTypes.bool.isRequired,
    onValidationChange: PropTypes.func.isRequired,
    isReadOnly: PropTypes.bool,
    isNew: PropTypes.bool,
    templateOptions: PropTypes.object.isRequired,
    assetOptions: PropTypes.object.isRequired,
    locationBusinessAreas: PropTypes.array.isRequired,
    showAssetTypeExclusionWarning: PropTypes.bool,
    showAssetSubTypeExclusionWarning: PropTypes.bool
};